import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Typography,
  Stack,
  LinearProgress,
  Alert,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import {
  OperationProcessFileService,
  StepAttachment,
  isImageFile,
} from '../../services/OperationProcessFileService';

export interface StepAttachmentsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  stepId: string;
  stepLabel?: string;
  /** 업로드/삭제 가능 여부. false 면 다운로드만 노출. */
  editable: boolean;
  /** 닫힐 때 호출되는 콜백(외부에서 카운트 갱신용). */
  onChanged?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * 단일 첨부 행. 이미지면 비동기 썸네일 로드.
 */
const AttachmentRow: React.FC<{
  projectId: string;
  stepId: string;
  attachment: StepAttachment;
  editable: boolean;
  onDeleted: () => void;
  setError: (msg: string | null) => void;
}> = ({ projectId, stepId, attachment, editable, onDeleted, setError }) => {
  const theme = useTheme();
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    if (attachment.isImage) {
      OperationProcessFileService.previewUrl(projectId, stepId, attachment.filename)
        .then((url) => {
          if (!alive) {
            URL.revokeObjectURL(url);
            return;
          }
          objectUrlRef.current = url;
          setThumbUrl(url);
        })
        .catch(() => {
          /* 썸네일 실패는 무시 (icon 으로 폴백) */
        });
    }
    return () => {
      alive = false;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [projectId, stepId, attachment.filename, attachment.isImage]);

  const handleDownload = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await OperationProcessFileService.downloadToFile(projectId, stepId, attachment.filename);
    } catch (err) {
      setError(`다운로드 실패: ${attachment.filename}`);
      console.error('[StepAttachmentsDialog] download failed', err);
    } finally {
      setBusy(false);
    }
  }, [projectId, stepId, attachment.filename, setError]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(`"${attachment.filename}" 을(를) 삭제하시겠습니까?`)) return;
    setBusy(true);
    setError(null);
    try {
      await OperationProcessFileService.remove(projectId, stepId, attachment.filename);
      onDeleted();
    } catch (err) {
      setError(`삭제 실패: ${attachment.filename}`);
      console.error('[StepAttachmentsDialog] delete failed', err);
    } finally {
      setBusy(false);
    }
  }, [projectId, stepId, attachment.filename, onDeleted, setError]);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.text.primary, 0.06),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={attachment.filename}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : attachment.isImage ? (
          <ImageIcon sx={{ color: 'text.secondary' }} />
        ) : (
          <InsertDriveFileIcon sx={{ color: 'text.secondary' }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={attachment.filename}
        >
          {attachment.filename}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatBytes(attachment.size)}
          {attachment.uploadedByName ? ` · ${attachment.uploadedByName}` : ''}
          {attachment.uploadDate ? ` · ${new Date(attachment.uploadDate).toLocaleString()}` : ''}
        </Typography>
      </Box>
      <Tooltip title="다운로드">
        <span>
          <IconButton size="small" onClick={handleDownload} disabled={busy}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {editable && (
        <Tooltip title="삭제">
          <span>
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={busy}
              sx={{
                color: 'text.disabled',
                '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1) },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};

const StepAttachmentsDialog: React.FC<StepAttachmentsDialogProps> = ({
  open,
  onClose,
  projectId,
  stepId,
  stepLabel,
  editable,
  onChanged,
}) => {
  const theme = useTheme();
  const [attachments, setAttachments] = useState<StepAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (!projectId || !stepId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await OperationProcessFileService.listByStep(projectId, stepId);
      setAttachments(list);
    } catch (err) {
      setError('첨부 목록 조회 실패');
      console.error('[StepAttachmentsDialog] list failed', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, stepId]);

  useEffect(() => {
    if (open) {
      refresh();
    }
  }, [open, refresh]);

  const handleClose = useCallback(() => {
    onChanged?.();
    onClose();
  }, [onChanged, onClose]);

  const upload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setError(null);
      setUploadProgress(0);
      try {
        await OperationProcessFileService.upload(projectId, stepId, files, setUploadProgress);
        await refresh();
      } catch (err) {
        setError('업로드 실패');
        console.error('[StepAttachmentsDialog] upload failed', err);
      } finally {
        setUploadProgress(null);
      }
    },
    [projectId, stepId, refresh],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files;
      if (!list) return;
      upload(Array.from(list));
      // 같은 파일 재선택 가능하게 reset
      e.target.value = '';
    },
    [upload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        upload(Array.from(files));
      }
    },
    [upload],
  );

  // 업로드는 보기 모드에서도 허용 — 첨부 파일 추가는 단순 자료 등록이지
  // 운영 프로세스 행 자체를 변경하는 것이 아니므로 view-only gate 에 묶지 않는다.
  // 삭제만 editable 일 때로 제한해 자료 유실 방지.
  const dropZone = useMemo(() => {
    return (
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          mt: 1,
          mb: 2,
          py: 3,
          px: 2,
          borderRadius: 1,
          border: '1.5px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">
          여기로 파일을 드래그하거나 클릭해 선택하세요
        </Typography>
        <Typography variant="caption" color="text.disabled">
          이미지·문서 모두 업로드 가능
        </Typography>
      </Box>
    );
  }, [dragOver, handleDrop, theme]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            단계 첨부 파일
          </Typography>
          {stepLabel && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {stepLabel}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {dropZone}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={handleFileInputChange}
        />

        {uploadProgress !== null && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              업로드 중... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <LinearProgress sx={{ width: '100%' }} />
          </Stack>
        ) : attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            첨부된 파일이 없습니다.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {attachments.map((att) => (
              <AttachmentRow
                key={att.filename}
                projectId={projectId}
                stepId={stepId}
                attachment={att}
                editable={editable}
                onDeleted={refresh}
                setError={setError}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StepAttachmentsDialog;
